import { z } from "zod";
import type {
  MetricGroupMetric,
  MetricGroupStream,
} from "./report-metric-groups";
import { reportMetricIdentities } from "./report-metric-groups";

/** Fixed report order; labels are server-owned rather than model-authored. */
export const EXEC_SUMMARY_SLOTS = [
  "what",
  "why_now",
  "cost",
  "return",
  "where_it_comes_from",
  "confidence",
  "softest_figure",
] as const;

export const ExecSummarySlotSchema = z.enum(EXEC_SUMMARY_SLOTS);
export type ExecSummarySlot = z.infer<typeof ExecSummarySlotSchema>;

export const EXEC_SUMMARY_SLOT_LABELS: Record<ExecSummarySlot, string> = {
  what: "What.",
  why_now: "Why now.",
  cost: "Cost.",
  return: "Return.",
  where_it_comes_from: "Where it comes from.",
  confidence: "Confidence.",
  softest_figure: "Softest figure.",
};

export const ReportFigureKeySchema = z.enum([
  "roi",
  "netSavings",
  "payback",
  "totalCost",
]);
export type ReportFigureKey = z.infer<typeof ReportFigureKeySchema>;

export const ReportValueRefSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("figure"), key: ReportFigureKeySchema }),
  z.object({ kind: z.literal("horizon") }),
  z.object({
    kind: z.literal("metric"),
    id: z.string(),
    field: z.enum(["label", "amount"]),
  }),
  z.object({
    kind: z.literal("assumption"),
    id: z.string(),
    field: z.enum(["name", "value"]),
  }),
  z.object({ kind: z.literal("pricingShape") }),
  z.object({
    kind: z.literal("topBenefit"),
    field: z.enum(["label", "amount"]),
  }),
  z.object({ kind: z.literal("softestInput"), field: z.literal("name") }),
  z.object({ kind: z.literal("evidenceMix") }),
  z.object({ kind: z.literal("uncitedInputCount") }),
  z.object({ kind: z.literal("assumptionCount") }),
]);
export type ReportValueRef = z.infer<typeof ReportValueRefSchema>;

export const ReportProseSegmentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), text: z.string() }),
  z.object({
    kind: z.literal("ref"),
    ref: ReportValueRefSchema,
    pending: z.string(),
  }),
]);
export type ReportProseSegment = z.infer<typeof ReportProseSegmentSchema>;

export const ReportProseAssertionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("within"),
    ref: ReportValueRefSchema,
    low: z.number().finite(),
    high: z.number().finite(),
  }),
  z.object({ kind: z.literal("topDriverIs"), metricId: z.string() }),
  z.object({ kind: z.literal("ordering"), metricIds: z.array(z.string()) }),
]);
export type ReportProseAssertion = z.infer<typeof ReportProseAssertionSchema>;

export const ExecSummaryArtifactBulletSchema = z.object({
  slot: ExecSummarySlotSchema,
  segments: z.array(ReportProseSegmentSchema),
  assertions: z.array(ReportProseAssertionSchema),
  sourceId: z.string().nullable(),
});
export type ExecSummaryArtifactBullet = z.infer<
  typeof ExecSummaryArtifactBulletSchema
>;

/**
 * The **browser-verifiable** slice of what the prose is about.
 *
 * `narrativeHash` (§6.1) is the authoritative staleness signal, but it is
 * derived from a prompt packet the browser never receives — brief prose,
 * strategic priorities, the intake projection — so the renderer cannot
 * recompute it and cannot use it to decide whether stored prose is still
 * about the current model. This structure is the part of that same input the
 * renderer *does* hold: it is rebuilt from the committed read model on both
 * sides and compared as sets.
 *
 * Deliberately only identifiers, never values. Every number in the summary is
 * a ref resolved live at render, so an amount change must not invalidate
 * prose; what must invalidate it is subject matter disappearing. The
 * per-segment `missing` / `unsafe` drops catch a *ref* to a deleted thing;
 * they cannot see a deleted value stream named in plain text. That is the gap
 * this closes, and it is why a mismatch discards the whole artifact rather
 * than one bullet.
 *
 * Scope, and why each set:
 * - `valueStreamIds` — streams are what bullets narrate in prose ("most of the
 *   return comes from avoided overtime"), and are never refs themselves.
 * - `metricIds` — `reportMetricIdentities`, i.e. the benefit groups the reader
 *   sees. Metric *structure* (a group appearing, splitting by cadence,
 *   collapsing) changes what the prose can truthfully say about the shape of
 *   the return.
 * - `assumptionIds` — the input set the confidence and softest-figure bullets
 *   characterize in prose ("two of the five inputs are still unconfirmed").
 *
 * Excluded on purpose: facts (the prose never enumerates them), labels (a
 * rename is not a structural change and would churn artifacts), and every
 * amount.
 *
 * `horizonYears` is the one deliberate exception to "identifiers, never
 * values", and it earns it. A bullet's `band` assertions are validated once,
 * at generation time, against the then-current horizon; the band gate permits
 * exactly the horizon-scaled refs (`horizon`, `figure_total_cost`,
 * `figure_net_savings`, metric and top-benefit amounts). Change the horizon
 * afterwards and those bands no longer hold, so `validateProseAssertions`
 * fails and the bullet is dropped **silently at render** — while
 * `narrativeHash` (values excluded by design) and the id sets above both still
 * call the artifact current. Carrying the horizon here is what lets the
 * renderer see that and fall back whole.
 */
export const ExecSummaryStructureSchema = z.object({
  valueStreamIds: z.array(z.string()),
  metricIds: z.array(z.string()),
  assumptionIds: z.array(z.string()),
  /**
   * The report horizon the bands were validated against
   * (`reportContractTermMonths` → years), or `null` when the projection was
   * uncomputed and there was no horizon to validate against.
   *
   * REQUIRED like `structure` itself, for the same reason: nothing is
   * persisted in production yet, so an old-shape artifact simply fails
   * `safeParse` and the renderer falls back.
   */
  horizonYears: z.number().nullable(),
});
export type ExecSummaryStructure = z.infer<typeof ExecSummaryStructureSchema>;

export interface ExecSummaryStructureSource {
  metrics?: readonly MetricGroupMetric[] | null;
  valueStreams: readonly (MetricGroupStream & { id: string })[];
  assumptions: readonly { id: string }[];
}

/**
 * Build the fingerprint from a committed read model. One implementation, two
 * callers — the exec-summary service when it writes the artifact, and
 * `toReportData` when it decides whether to trust it. A fingerprint that
 * cannot be computed identically on both sides is worse than none.
 *
 * Stored sorted so the persisted JSON is stable, but comparison never relies
 * on that: `execSummaryStructureMatches` compares sets.
 */
export function buildExecSummaryStructure(
  view: ExecSummaryStructureSource,
  /**
   * The resolved report horizon in years — `reportContractTermMonths` divided
   * down by `monthsToReportYears` — or `null` when there is no projection to
   * take one from. A separate argument rather than a view field because the
   * horizon lives on the investment curve, not on the read model: the service
   * takes it from `reportValues`, the renderer from the timeline it is about
   * to draw.
   */
  horizonYears: number | null,
): ExecSummaryStructure {
  return {
    horizonYears,
    valueStreamIds: [...new Set(view.valueStreams.map((s) => s.id))].sort(),
    metricIds: [
      ...new Set(reportMetricIdentities(view).map((metric) => metric.id)),
    ].sort(),
    assumptionIds: [...new Set(view.assumptions.map((a) => a.id))].sort(),
  };
}

function sameIdSet(left: readonly string[], right: readonly string[]): boolean {
  const seen = new Set(left);
  if (seen.size !== new Set(right).size) return false;
  return right.every((id) => seen.has(id));
}

/**
 * A horizon change invalidates the artifact — but only when both sides
 * actually have a horizon.
 *
 * `null` on either side means "no projection", and treating null-vs-number as
 * a mismatch would discard the artifact on the uncomputed → computed
 * transition. That transition is already covered server-side:
 * `projectionUncomputed` is part of `narrativeHash`, so a regeneration fires
 * and rewrites the prose anyway. Discarding here as well would be redundant at
 * best, and at worst would strand a perfectly good artifact as
 * permanently-discarded whenever no regeneration trigger follows — the horizon
 * is *not* in `narrativeHash`, so nothing would ever refresh the stored value.
 *
 * Comparing only two real horizons keeps the check pointed at the defect it
 * exists for: a 3 → 5 year change silently invalidating bands.
 */
function sameHorizon(left: number | null, right: number | null): boolean {
  if (left === null || right === null) return true;
  return left === right;
}

/** Order-independent set equality across all three id sets, plus the horizon. */
export function execSummaryStructureMatches(
  left: ExecSummaryStructure,
  right: ExecSummaryStructure,
): boolean {
  return (
    sameHorizon(left.horizonYears, right.horizonYears) &&
    sameIdSet(left.valueStreamIds, right.valueStreamIds) &&
    sameIdSet(left.metricIds, right.metricIds) &&
    sameIdSet(left.assumptionIds, right.assumptionIds)
  );
}

export const ExecSummaryArtifactSchema = z.object({
  version: z.literal("v1"),
  generatedAt: z.string(),
  model: z.string(),
  narrativeHash: z.string(),
  /**
   * REQUIRED, not optional, on purpose. The schema is `version: "v1"` and no
   * artifact is persisted in production yet, so demanding the field costs
   * nothing today; an old-shape artifact simply fails `safeParse` and the
   * renderer falls back silently, which is the behaviour we want anyway.
   * Making it optional later — once rows exist — would mean an artifact that
   * parses but cannot be verified, i.e. exactly the unverifiable-prose hole
   * this field exists to close.
   */
  structure: ExecSummaryStructureSchema,
  bullets: z.array(ExecSummaryArtifactBulletSchema),
});
export type ExecSummaryArtifact = z.infer<typeof ExecSummaryArtifactSchema>;

/**
 * How long a `running` executive-summary claim may stand before readers treat
 * it as abandoned.
 *
 * Generation is fire-and-forget: the intake-snapshot path detaches the call
 * inside a request-scoped handler, so an instance reclaimed mid-generation
 * leaves `running` behind with no writer left to settle it. Nothing *blocks*
 * on that row — the claim write is unconditional (steal-on-write) and the
 * loser is rejected by the `attemptId` compare-and-set — so the defect is only
 * that `running` is otherwise unbounded and therefore untrustworthy as a
 * signal. This TTL bounds it without a reaper: the state stays exactly as
 * written, and every reader interprets it through
 * `execSummaryGenerationIsStale`.
 *
 * Sized well above a worst-case attempt (deterministic brief rebuild +
 * committed-view read + one Sonnet call with provider retries).
 */
export const EXEC_SUMMARY_GENERATION_TTL_MS = 10 * 60 * 1000;

/**
 * The part of a generation stage row a reader needs to interpret it.
 *
 * Structural rather than the API's zod-derived `ExecSummaryGenerationStageState`
 * so the browser can share this predicate: `@atlas/api` is a type-only import
 * in `apps/web`, and pulling a runtime value from it would drag the server
 * bundle into the client. The contract re-exports both symbols, so the API
 * keeps its existing import path.
 */
export interface ExecSummaryGenerationProgress {
  status: string;
  startedAt: string | null;
}

/**
 * True when a claim says `running` but has outlived
 * {@link EXEC_SUMMARY_GENERATION_TTL_MS} — i.e. its writer is gone and the row
 * describes nothing in flight. A `running` claim with an unparseable or absent
 * `startedAt` cannot be bounded at all, so it is stale on sight.
 */
export function execSummaryGenerationIsStale(
  state: ExecSummaryGenerationProgress | null,
  now: Date = new Date(),
): boolean {
  if (!state || state.status !== "running") return false;
  const startedAt = state.startedAt ? Date.parse(state.startedAt) : Number.NaN;
  if (Number.isNaN(startedAt)) return true;
  return now.getTime() - startedAt >= EXEC_SUMMARY_GENERATION_TTL_MS;
}

/**
 * True while a claim describes generation that is genuinely still in flight —
 * the condition a reader polls on.
 *
 * `queued` and `running` are the live states; a `running` claim past the TTL is
 * abandoned, not live, and must not hold a poll open forever.
 */
export function execSummaryGenerationIsActive(
  state: ExecSummaryGenerationProgress | null,
  now: Date = new Date(),
): boolean {
  if (!state) return false;
  if (state.status === "queued") return true;
  return (
    state.status === "running" && !execSummaryGenerationIsStale(state, now)
  );
}
